// App-shell error boundary (ADM-2).
//
// A single render throw anywhere in a routed page previously unmounted the ENTIRE
// SPA — nav included — exactly when the backend returns an unexpected shape (the
// System Health crash was one instance of this class). Wrapping the routed
// <Outlet/> keeps the sidebar/nav alive and shows a recoverable error card with a
// reload, so the operator's recovery tool survives a bad API payload.
import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
  /** Bump this (e.g. the route pathname) to auto-reset the boundary on navigation. */
  resetKey?: string;
}
interface State {
  error: Error | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidUpdate(prev: Props) {
    // Clear the error when the route changes so navigating away from a broken
    // page recovers without a full reload.
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Admin console render error:", error, info.componentStack);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="mx-auto max-w-lg py-12">
          <div className="rounded-lg border border-red-900 bg-red-950 p-4">
            <h2 className="text-sm font-semibold text-red-200">Something went wrong</h2>
            <p className="mt-1 text-sm text-red-300">
              This page hit an unexpected error and stopped rendering. The rest of the
              console is still usable — try another section, or reload.
            </p>
            <pre className="mt-3 max-h-40 overflow-auto rounded bg-red-950/60 p-2 font-mono text-xs text-red-300/80">
              {this.state.error.message}
            </pre>
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                onClick={() => this.setState({ error: null })}
                className="rounded border border-red-800 px-3 py-1.5 text-sm text-red-200 hover:bg-red-900/40"
              >
                Try again
              </button>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="rounded bg-zinc-700 px-3 py-1.5 text-sm text-white hover:bg-zinc-600"
              >
                Reload
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
